<script setup>
import {reactive, inject, ref, onMounted, computed} from "vue";
//import functions
//import components
import {Icon} from "@iconify/vue";
import {Dialog, InputText, FloatLabel, Select} from "primevue";
//import stores
import {useWatchdogStore} from "@/stores/watchdog.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";
import {and, helpers, url} from "@vuelidate/validators";
import showAlert from "@/utils/showAlert.js";
import {useVuelidate} from "@vuelidate/core";

//stores
const watchdogStore = useWatchdogStore();

//loading flags
const {
  saving,
  fetching,
  loading,
  setSaving,
  setFetching,
  setLoading,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)


//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  offer: {
    type: Object,
    required: false
  }
})

//injects
const visible = inject('isShowAddOfferModal')

//component variables
const stores = [
  {
    value: 'DealHub USD',
    label: 'DealHub USD',
  },
  {
    value: 'Incepto USD',
    label: 'Incepto USD',
  }
]
const types = [
  {
    value: 'Boost',
    label: 'Boost',
  }
]
const offer = reactive({
  "offerID": props.offer?.offerID ? props.offer.offerID : "",
  "store": props.offer?.store ? props.offer.store : "",
  "type": props.offer?.type ? props.offer.type : "Boost",
  "managementLink": props.offer?.managementLink ? props.offer.managementLink : "",
})

const rules = computed(() => {
  return {
    "managementLink": {url: and(url, helpers.regex(/^https?:\/\//i))}
  };
});
const $vuelidate = useVuelidate(rules, offer);

//component functions
const onSave = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  try {
    setLoading(true)
    let res;
    if (props.offer?.offerID) {
      res = await watchdogStore.action_updateOffer(offer)
    } else {
      res = await watchdogStore.action_addOffer(offer)
    }

    if (res.result === true) {
      if (props.offer?.offerID) {
        showMessage(`Offer ${props.offer.offerID} updated.`, 'success');
      } else {
        showMessage('New Offer Added.', 'success');
      }
      emit('refresh')
      visible.value = false
    } else {
      showMessage(res, 'error')
    }
  } catch
      (e) {
    showMessage(e, 'error')
  } finally {
    setLoading(false)
  }
}

//hooks
onMounted(() => {

})

//provides

</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)] !px-3">
    <template #header>
      <div class="text-base py-3">
        {{ props.offer?.offerID ? `Edit Offer ${props.offer.offerID}` : 'Add New Offer' }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button type="button" class="text-white-dark outline-none hover:text-red-500" @click="closeCallback">
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-6 overflow-y-auto pt-4 px-2 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex flex-col lg:flex-row gap-4">
        <FloatLabel variant="on">
          <InputText v-if="props.offer?.offerID" id="offerID" :value="offer.offerID" autocomplete="off" disabled
                     readonly
                     class="!bg-transparent w-full min-w-28 rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
          <InputText v-else id="offerID" v-allow-digits v-model="offer.offerID" autocomplete="off"
                     :disabled="props.offer?.offerID"
                     class="!bg-transparent w-full min-w-28 rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
          <label for="offerID" class="!bg-slate-900">Offer ID</label>
        </FloatLabel>
        <FloatLabel class="w-full custom-select"
                    variant="on">
          <Select v-model="offer.store" inputId="store" :options="stores" optionLabel="label"
                  option-value="value"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                  input-class="!text-xs 2xl:!text-sm"
                  overlayClass="custom-select-overlay"/>
          <label for="store" class="!bg-slate-900">Store</label>
        </FloatLabel>
        <FloatLabel class="w-full custom-select"
                    variant="on">
          <Select v-model="offer.type" inputId="type" :options="types" optionLabel="label"
                  option-value="value"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                  input-class="!text-xs 2xl:!text-sm"
                  overlayClass="custom-select-overlay"/>
          <label for="type" class="!bg-slate-900">Type</label>
        </FloatLabel>
      </div>
      <div class="flex flex-col gap-0.5">
        <FloatLabel variant="on">
          <InputText id="managementLink" v-model.trim="offer.managementLink" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
          <label for="managementLink" class="!bg-slate-900">Management Link</label>
        </FloatLabel>
        <template v-if="$vuelidate.managementLink.$error">
          <p class="text-red-300 mt-1 text-xs" v-if="!$vuelidate.managementLink.url.$response">
            Enter a valid URL starting with http:// or https://.
          </p>
        </template>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving || loading">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style>
.swal2-container.swal2-center.swal2-backdrop-show {
  z-index: 100000;
}
</style>
